<template>
	<EventLayout :event="event" :subtitle="offer.title" :return-url="route('events.bundles.edit', [event.id, bundle.id])" :return-label="bundle.name">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="save">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<div class="grid grid-cols-2 w-full gap-3">
			<LabeledObject class="col-span-2" required>
				<template #label>{{ trans('general.title') }}</template>
				<input v-model="offer.title" type="text">
			</LabeledObject>
			<LabeledObject class="col-span-2">
				<template #label>{{ trans('general.description') }}</template>
				<TextEditor v-model="offer.description" bordered/>
			</LabeledObject>
			<Card :title="trans('events.bundles.offers.maximum')" class="col-span-2">
				<div class="flex items-end gap-3">
					<IconNumberInput v-model.number="offer.maximum" icon-class="fa-solid fa-ticket"/>
					<Switch v-model.number="offer.active" :label="trans('events.bundles.offers.active')"/>
				</div>
			</Card>
			<Card :title="trans('events.payables.prices.early')">
				<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.general') }}</template>
						<NumberInput v-model.number="offer.early_general_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.local') }}</template>
						<NumberInput v-model.number="offer.early_local_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.international') }}</template>
						<NumberInput v-model.number="offer.early_international_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.representative') }}</template>
						<NumberInput v-model.number="offer.early_representative_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.organizer') }}</template>
						<NumberInput v-model.number="offer.early_organizer_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.workgroup') }}</template>
						<NumberInput v-model.number="offer.early_workgroup_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.board') }}</template>
						<NumberInput v-model.number="offer.early_board_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.dinosaur') }}</template>
						<NumberInput v-model.number="offer.early_dinosaur_price"/>
					</LabeledObject>
				</div>
			</Card>
			<Card :title="trans('events.payables.prices.late')">
				<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.general') }}</template>
						<NumberInput v-model.number="offer.general_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.local') }}</template>
						<NumberInput v-model.number="offer.local_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.international') }}</template>
						<NumberInput v-model.number="offer.international_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.representative') }}</template>
						<NumberInput v-model.number="offer.representative_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.organizer') }}</template>
						<NumberInput v-model.number="offer.organizer_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.workgroup') }}</template>
						<NumberInput v-model.number="offer.workgroup_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.board') }}</template>
						<NumberInput v-model.number="offer.board_price"/>
					</LabeledObject>
					<LabeledObject>
						<template #label>{{ trans('events.payables.prices.dinosaur') }}</template>
						<NumberInput v-model.number="offer.dinosaur_price"/>
					</LabeledObject>
				</div>
			</Card>
		</div>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import Card from '@/Components/UI/Cards/Card.vue'
import EventLayout from '@/Layouts/EventLayout.vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import {type Bundle} from '@/Types/Events/Bundles/Bundle'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import TextEditor from '@/Components/UI/Editor/TextEditor.vue'
import {type Offer} from '@/Types/Events/Bundles/Offers/Offer'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import IconNumberInput from '@/Components/UI/Inputs/IconNumberInput.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	bundle:Bundle,
	event:Event,
	offer:Offer,
}>()

const bundle:Ref<Bundle> = ref(props.bundle)
const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const offer:Ref<Offer> = ref(props.offer)

function save():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.bundles.offers.update', [event.value.id, bundle.value.id, offer.value.id]), offer.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>
